import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { GenericApiService } from '@services/generic-api.service';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, finalize, filter } from 'rxjs/operators';

export interface AutocompleteCellEditorParams {
  optionsUrl: string;
  defaultOptionsUrl?: string;
  defaultOptionKeys?: string[];
  formtype: string;
}

interface OptionItem {
  rid?: string;
  name?: string;
  display_value?: string;
  value?: any;
  title?: string;
}

@Component({
  selector: 'app-autocomplete-cell-editor',
  standalone: true,
  imports: [FormsModule, MatAutocompleteModule, MatInputModule, MatFormFieldModule, MatProgressSpinnerModule],
  templateUrl: './autocomplete-cell-editor.component.html',
  styleUrl: './autocomplete-cell-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutocompleteCellEditorComponent implements ICellEditorAngularComp {
  private readonly genericApiService = inject(GenericApiService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly editorInput = viewChild<ElementRef<HTMLInputElement>>('editorInput');
  private readonly autoTrigger = viewChild(MatAutocompleteTrigger);

  searchText = '';
  options: OptionItem[] = [];
  loading = false;

  private selectedOption: OptionItem | null = null;
  private params!: ICellEditorParams & AutocompleteCellEditorParams;
  private optionsUrl = '';
  private defaultOptionsUrl = '';
  private formtype = '';
  private rowData: any;
  private readonly search$ = new Subject<string>();

  agInit(params: ICellEditorParams & AutocompleteCellEditorParams): void {
    this.params = params;
    this.optionsUrl = params.optionsUrl || '';
    this.defaultOptionsUrl = params.defaultOptionsUrl || params.optionsUrl || '';
    this.formtype = params.formtype || 'autocomplete';
    this.rowData = params.data;

    const currentValue = params.value;
    if (currentValue && typeof currentValue === 'object') {
      this.searchText = currentValue.display_value || currentValue.name || currentValue.title || '';
    } else {
      this.searchText = currentValue ?? '';
    }

    this.search$
      .pipe(
        debounceTime(300),
        filter((term) => term.length >= 1 || this.formtype === 'autoselect'),
        switchMap((term) => {
          this.loading = true;
          const url = this.resolveUrl(term ? this.optionsUrl : this.defaultOptionsUrl, term);
          if (!url) {
            this.loading = false;
            return [];
          }
          return this.genericApiService.getBaseData(url).pipe(finalize(() => (this.loading = false)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: any) => {
          this.options = this.extractOptions(res);
        },
        error: () => {
          this.loading = false;
          this.options = [];
        }
      });
  }

  afterGuiAttached(): void {
    const input = this.editorInput()?.nativeElement;
    if (input) {
      input.focus();
      input.select();
    }

    if (this.formtype === 'autoselect' || this.formtype === 'tile-autoselect') {
      this.search$.next('');
    }
  }

  getValue(): any {
    if (this.selectedOption) {
      return (
        this.selectedOption.display_value ||
        this.selectedOption.name ||
        this.selectedOption.title ||
        this.selectedOption.value
      );
    }
    return this.searchText;
  }

  isPopup(): boolean {
    return false;
  }

  onSearchChange(value: string): void {
    this.selectedOption = null;
    this.search$.next(value);
  }

  onOptionSelected(event: any): void {
    this.selectedOption = event.option.value;
    this.searchText = this.displayFn(this.selectedOption);
    setTimeout(() => this.params.api.stopEditing());
  }

  onEscape(): void {
    this.params.api.stopEditing(true);
  }

  onEnterKey(event: Event): void {
    if (this.autoTrigger()?.panelOpen) {
      return;
    }
    this.params.api.stopEditing();
  }

  displayFn = (option: OptionItem | string | null): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.display_value || option.name || option.title || String(option.value || '');
  };

  private resolveUrl(urlTemplate: string, keyword: string): string | null {
    if (!urlTemplate) return null;

    let url = urlTemplate;

    if (url.includes('<keyword>')) {
      url = url.replace('<keyword>', encodeURIComponent(keyword));
    }

    const placeholderRegex = /<([^>]+)>/g;
    let match;
    while ((match = placeholderRegex.exec(url)) !== null) {
      const placeholder = match[1];
      const resolvedValue = this.resolveFieldValue(placeholder);
      if (!resolvedValue) {
        return null;
      }
      url = url.replace(`<${placeholder}>`, resolvedValue.replace('#', ''));
    }

    if (keyword && !urlTemplate.includes('<keyword>')) {
      url = `${url}/${encodeURIComponent(keyword)}`;
    }

    return url;
  }

  private resolveFieldValue(placeholder: string): string | null {
    if (!this.rowData?.row) return null;

    for (const item of this.rowData.row) {
      const itemKey = item.key.replace('$', '.').split('.').slice(-2).join('.');
      if (itemKey === placeholder || item.key.endsWith(placeholder)) {
        return item.rid || item.value || null;
      }
    }

    const flatKey = placeholder.split('.').pop()!;
    if (this.rowData[flatKey]) {
      const val = this.rowData[flatKey];
      return typeof val === 'object' ? val.rid || val.value : String(val);
    }

    return null;
  }

  private extractOptions(res: any): OptionItem[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    return res.search_results || res.results || res.lines || res.impacts || res.radio || res.files || res.users || [];
  }
}
